import { useRef, useEffect, useLayoutEffect, useState, useMemo } from "react";
import RemToPx from "__AxiomComponents/RemToPx/RemToPx";
import intersectionObserver from "intersection-observer";

// Checks if the current widget is visible on the screen
function useIsVisible(elementRef, threshold = 0.1) {
  const element = elementRef.current;
  const [isVisible, updateVisibility] = useState(false);
  const observerRef = useRef();

  useLayoutEffect(() => {
    if (element) {
      let defaultIntersectionObserver = window.IntersectionObserver || intersectionObserver;
      const observerCallback = ([entry]) => {
        if (entry.intersectionRatio >= threshold && !isVisible) {
          updateVisibility(true);
        }
      };
      observerRef.current = new defaultIntersectionObserver(observerCallback, {
        root: null,
        threshold: threshold,
      });
      observerRef.current.observe(element);
    }
  }, [isVisible, element, observerRef, threshold]);

  return isVisible;
}

// Delays the rendering of the widget by a certain timeout to prevent loading all
// widgets in-case of a fast scroll on the view
function useDelayedRendering(timeout) {
  const [shouldRender, setRender] = useState(false);

  useEffect(() => {
    const renderTimer = setTimeout(() => setRender(true), timeout);
    return () => clearTimeout(renderTimer);
  }, [timeout]);

  return shouldRender;
}

function getArray(object, depth, arr) {
  Object.keys(object).forEach((d) => {
    if (typeof object[d] === "string" || typeof object[d] === "number")
      arr.push([d, object[d], depth]);
    else {
      arr.push([d, "", depth]);
      getArray(object[d], depth + 1, arr);
    }
  });
  return arr;
}

function getToolTipData(widgetInfo) {
  let dataArray = getArray(widgetInfo, 0, []);
  return (
    <div>
      {dataArray.map((data) => {
        const paddingLeft = data[2] > 1 ? (data[2] - 1) * 10 : 0;
        const style = { paddingLeft: RemToPx(paddingLeft) };
        const text = data[0] + " : " + data[1];
        return data[2] > 0 ? (
          <div className="data-line" style={style}>
            &#8226; {text}
          </div>
        ) : (
          <div className="data-line" style={style}>
            {text}
          </div>
        );
      })}
    </div>
  );
}

// Create options data for the download data component
function useDownloadData(viewDetails, KPIList, KPIWiseConfigs) {
  const options = useMemo(() => {
    const downloadOptions = new Map();

    if (viewDetails && KPIWiseConfigs) {
      for (let kpi in KPIList) {
        const currentKPI = KPIWiseConfigs[KPIList[kpi].mainkpiid];
        if (currentKPI) {
          currentKPI.kpiCalculationConfig.viewDetails.forEach((v) => {
            const currentViewDetails = viewDetails[v.viewID];
            if (currentViewDetails) {
              if (!downloadOptions.has(v.viewID)) {
                downloadOptions.set(v.viewID, {
                  key: v.viewID,
                  displayName: currentViewDetails.tableName,
                  metadata: currentViewDetails,
                });
              }
            } else {
              console.warn("View metadata not found in the viewDetails");
              console.log({ currentView: v, viewDetails });
            }
          });
        } else {
          console.warn("KPI in widget setting KPIList not found in the KPIWiseConfigs");
          console.log({ KPIList, KPIWiseConfigs, currentKPI });
        }
      }
    }

    return [...downloadOptions.values()];
  }, [viewDetails, KPIList, KPIWiseConfigs]);

  return options;
}

export { useIsVisible, useDelayedRendering, getToolTipData, useDownloadData };
